import React, { createContext, Dispatch, SetStateAction, useEffect, useState } from "react";
import { FeatureFlagProviderName, FeatureFlagProvidersMap } from "../components/types";
import { FeatureFlagContextProviderProps } from "./types";

export const FeatureFlagStateContext = createContext<FeatureFlagProvidersMap<FeatureFlagProviderName>>(undefined);
FeatureFlagStateContext.displayName = "FeatureFlagStateContext";

export const FeatureFlagStateSetterContext =
  createContext<Dispatch<SetStateAction<FeatureFlagProvidersMap<FeatureFlagProviderName>>>>(undefined);
FeatureFlagStateSetterContext.displayName = "FeatureFlagStateSetterContext";

/**
 * A context provider that provides a global feature flags support for its children sub-tree.
 * It allows conditionally rendering UI elements and executing application logic based on feature flags.
 *
 * It accepts a feature flag providers object as prop. It holds this object as state. It renders its `children` wrapped
 * in context providers. These context providers make this state and the associated state setter function available to any
 * component rendered within their children sub-tree. These can be accessed using the following companion hooks.
 *
 * ### `useFeatureFlag`
 *
 * This hook returns a feature flag value based on a feature flag query.
 *
 * ### `useIsFeatureFlagEnabled`
 *
 * This hook returns a boolean indicating if a feature flag is enabled based on a feature flag query and a list of
 * acceptable flag values.
 */
export const FeatureFlagContextProvider = <F extends FeatureFlagProviderName = FeatureFlagProviderName>({
  providers: providersProp,
  children,
}: FeatureFlagContextProviderProps<F>) => {
  const [featureFlagProviders, setFeatureFlagProviders] = useState(providersProp);

  useEffect(() => {
    setFeatureFlagProviders((providersState) => (providersProp !== providersState ? providersProp : providersState));
  }, [providersProp]);

  return (
    <FeatureFlagStateContext.Provider value={featureFlagProviders}>
      <FeatureFlagStateSetterContext.Provider value={setFeatureFlagProviders}>
        {children}
      </FeatureFlagStateSetterContext.Provider>
    </FeatureFlagStateContext.Provider>
  );
};
